// Tier 1 Progress dashboard math helpers (#1–#4). All pure functions
// over already-fetched data — no I/O, no clock reads (except where a
// `nowMs` parameter is passed in). See docs/progress-redesign.md.
import { DAY_MS } from "./freshness.js";
import type { Exercise } from "./schemas/exercise.js";
import type { MovementPattern } from "./schemas/muscle.js";
import type { MuscleSlug, PainSeverity } from "./bodymap.js";
import type { TimedSessionResult } from "./progressTypes.js";
import {
  catalogBySlug,
  catalogVolumeToBodymap,
  normalizeVolumeByMuscle,
} from "./volumeByMuscle.js";
import { EFFORT_SUBMAXIMAL_RIR } from "./effortScale.js";

// Window helper. Sessions newer than `nowMs - windowDays * DAY_MS`.
const inWindow = (
  s: TimedSessionResult,
  nowMs: number,
  windowDays: number,
): boolean => s.startedAtMs >= nowMs - windowDays * DAY_MS;

// =========================================================================
// Tier 1 #1 — Volume per movement pattern.
// =========================================================================
// Sums sets + volume across every ExerciseResult in `sessions`,
// distributed to its Exercise.movementPattern via `catalog`. Returns
// zero entries for patterns that didn't appear in the window so the UI
// can render a complete ring chart without holes.

const ALL_PATTERNS: MovementPattern[] = [
  "squat",
  "hinge",
  "horizontal-push",
  "vertical-push",
  "horizontal-pull",
  "vertical-pull",
  "lunge",
  "carry",
  "core",
  "isolation",
  "cardio",
  "other",
];

// `hardSets` is the effort-filtered subset of `sets` — sets trained at
// RIR ≤ EFFORT_SUBMAXIMAL_RIR (the hypertrophy accumulator unit, AP-2).
// Summed from ExerciseResult.hardSetCount, which the SessionResult compute
// derives from per-set RIR (the aggregate has no per-set effort otherwise).
export type PatternVolume = { sets: number; volumeKg: number; hardSets: number };

export const volumeByPattern = (
  sessions: TimedSessionResult[],
  catalog: ReadonlyArray<Exercise>,
  nowMs: number,
  // PG-1 period selector: windowDays is now caller-chosen (7/14/30/90/…), not
  // the old fixed 7|28. The window math already treats it as a plain number;
  // widening the type keeps every existing 7/28 callsite valid.
  windowDays: number,
): Record<MovementPattern, PatternVolume> => {
  const bySlug = catalogBySlug(catalog);
  const acc: Record<MovementPattern, PatternVolume> = Object.fromEntries(
    ALL_PATTERNS.map((p) => [p, { sets: 0, volumeKg: 0, hardSets: 0 }]),
  ) as Record<MovementPattern, PatternVolume>;
  for (const s of sessions) {
    if (!inWindow(s, nowMs, windowDays)) continue;
    for (const er of s.result.exerciseResults) {
      const pattern = bySlug.get(er.exerciseSlug)?.movementPattern ?? "other";
      const bucket = acc[pattern];
      bucket.sets += er.setCount;
      bucket.volumeKg += er.totalVolumeKg;
      // Legacy rows (pre-hardSetCount) parse the field as 0 via the schema
      // default, so this is safe; `?? 0` guards raw objects in tests too.
      bucket.hardSets += er.hardSetCount ?? 0;
    }
  }
  return acc;
};

// =========================================================================
// Tier 1 #2 — E1RM regression detection.
// =========================================================================
// Linear least-squares fit over the points in the trailing N-week
// window. Slope is kg/week (sign-bearing). R² gives the fit confidence;
// noisy data (R² < 0.2) is downgraded to "plateau" regardless of slope
// so a single outlier can't trigger a regression alert.

export type RegressionResult = {
  slopePerWeek: number;
  rSquared: number;
  pctChange: number; // signed; over the windowed segment
  flag: "regressing" | "plateau" | "progressing";
  pointCount: number;
};

// A drop in e1RM is only a true regression when the recent work was near-
// maximal. Two ways the slope gets downgraded "regressing" → "plateau":
//   (1) RELATIVE — recent half trained ≥ this many RIR further from failure
//       than the baseline half (a clear back-off vs how it used to be lifted).
export const EFFORT_REGRESSION_RIR_DROP = 1.5;
//   (2) ABSOLUTE — recent half is itself clearly submaximal (mean RIR ≥ this):
//       a set with reps left in reserve UNDER-states capacity, so its e1RM
//       can't prove a lower ceiling (same asymmetry as the MAX-lower path).
//       This catches the real leg-press case where the OLD sessions carry no
//       RIR at all (so the relative test can't fire) but the recent block is
//       explicitly RIR 3 (Antenore 2026-06-26).
// Canonical definition now lives in effortScale.ts (shared with the AP-2
// hard-set accumulator); re-exported here for existing importers.
export { EFFORT_SUBMAXIMAL_RIR };

// Mean RIR over the points that actually carry one; null when none do
// (legacy points before topSetRir, or sets logged without RIR) → the
// effort override stays inactive and the slope-only verdict stands.
const meanRir = (
  pts: ReadonlyArray<{ rir?: number | null }>,
): number | null => {
  let sum = 0;
  let count = 0;
  for (const p of pts) {
    if (p.rir != null) {
      sum += p.rir;
      count += 1;
    }
  }
  return count > 0 ? sum / count : null;
};

export const detectRegression = (
  // `rir` (RIR of the top-e1RM set) is optional: callers that supply it
  // get effort-aware downgrading; those that don't keep the slope-only
  // behaviour (back-compat).
  points: ReadonlyArray<{ atMs: number; kgPerRep1: number; rir?: number | null }>,
  nowMs: number,
  // PG-1 period selector: was 8|12; now caller-chosen (windowDays/7). The
  // slope fit already returns "plateau" below 3 points, so short windows
  // degrade gracefully instead of throwing.
  windowWeeks: number,
): RegressionResult => {
  const windowMs = windowWeeks * 7 * DAY_MS;
  const cutoff = nowMs - windowMs;
  const filtered = points
    .filter((p) => p.atMs >= cutoff && p.kgPerRep1 > 0)
    .sort((a, b) => a.atMs - b.atMs);
  if (filtered.length < 3) {
    return {
      slopePerWeek: 0,
      rSquared: 0,
      pctChange: 0,
      flag: "plateau",
      pointCount: filtered.length,
    };
  }
  const n = filtered.length;
  const weekMs = 7 * DAY_MS;
  const xs = filtered.map((p) => (p.atMs - filtered[0]!.atMs) / weekMs);
  const ys = filtered.map((p) => p.kgPerRep1);
  const xMean = xs.reduce((a, b) => a + b, 0) / n;
  const yMean = ys.reduce((a, b) => a + b, 0) / n;
  let num = 0;
  let den = 0;
  for (let i = 0; i < n; i++) {
    num += (xs[i]! - xMean) * (ys[i]! - yMean);
    den += (xs[i]! - xMean) ** 2;
  }
  const slope = den === 0 ? 0 : num / den;
  const intercept = yMean - slope * xMean;
  let ssRes = 0;
  let ssTot = 0;
  for (let i = 0; i < n; i++) {
    const yi = ys[i]!;
    const yhat = intercept + slope * xs[i]!;
    ssRes += (yi - yhat) ** 2;
    ssTot += (yi - yMean) ** 2;
  }
  const rSquared = ssTot === 0 ? 1 : Math.max(0, 1 - ssRes / ssTot);
  const first = ys[0]!;
  const last = ys[n - 1]!;
  const pctChange = first === 0 ? 0 : (last - first) / first;
  // R² < 0.2 = too noisy to trust the slope; force plateau so a single
  // spike doesn't pop a regression alert.
  let flag: RegressionResult["flag"];
  if (rSquared < 0.2) {
    flag = "plateau";
  } else if (pctChange < -0.03) {
    flag = "regressing";
  } else if (pctChange > 0.03) {
    flag = "progressing";
  } else {
    flag = "plateau";
  }
  // Effort-aware override: a "regressing" slope is only trustworthy when
  // the recent work was near-maximal. Downgrade to "plateau" when the
  // recent half was either (1) clearly submaximal on its own (mean RIR ≥
  // EFFORT_SUBMAXIMAL_RIR — its e1RM understates capacity, can't prove a
  // lower ceiling), or (2) trained ≥ EFFORT_REGRESSION_RIR_DROP RIR lighter
  // than the baseline half. Needs RIR on the relevant side; absent ⇒ the
  // slope verdict stands (back-compat with pre-topSetRir points).
  if (flag === "regressing") {
    const mid = Math.floor(n / 2);
    const baselineRir = meanRir(filtered.slice(0, mid));
    const recentRir = meanRir(filtered.slice(mid));
    const recentSubmaximal =
      recentRir !== null && recentRir >= EFFORT_SUBMAXIMAL_RIR;
    const lighterThanBaseline =
      baselineRir !== null &&
      recentRir !== null &&
      recentRir - baselineRir >= EFFORT_REGRESSION_RIR_DROP;
    if (recentSubmaximal || lighterThanBaseline) {
      flag = "plateau";
    }
  }
  return { slopePerWeek: slope, rSquared, pctChange, flag, pointCount: n };
};

// =========================================================================
// Tier 1 #3 — Pain × volume overlay.
// =========================================================================
// Returns two parallel series the UI can stack on the same timeline:
// week-bucketed volume for the muscle, and the pain entries that
// touched it. Volume bucket: ISO-week-of-year would mismatch the
// rolling-window convention (feedback_rolling_window_not_iso_week);
// we bucket by 7-day offsets from `nowMs` so the rightmost bar is
// always "current week".

export type PainVolumeOverlay = {
  // Volume per 7-day rolling bucket, ordered oldest → newest. Length
  // equals ceil(windowDays / 7).
  volumeBuckets: ReadonlyArray<{ bucketEndMs: number; volumeKg: number }>;
  // Pain events touching this muscle in the window, ordered by time.
  // Severity reflects the value of `map[muscleSlug]` at that point.
  painEvents: ReadonlyArray<{ atMs: number; severity: PainSeverity }>;
};

export const painVolumeOverlay = (
  sessions: TimedSessionResult[],
  painHistory: ReadonlyArray<{
    at: string;
    map: Partial<Record<MuscleSlug, PainSeverity>>;
  }>,
  muscleSlug: MuscleSlug,
  nowMs: number,
  windowDays: number,
): PainVolumeOverlay => {
  const cutoff = nowMs - windowDays * DAY_MS;
  const bucketCount = Math.ceil(windowDays / 7);
  const bucketEnds: number[] = [];
  for (let i = bucketCount - 1; i >= 0; i--) {
    bucketEnds.unshift(nowMs - i * 7 * DAY_MS);
  }
  const volumes = bucketEnds.map(() => 0);
  for (const s of sessions) {
    if (s.startedAtMs < cutoff || s.startedAtMs > nowMs) continue;
    // Slice #4: volumeByMuscle keys on catalog vocab; muscleSlug here
    // is bodymap (painMap-side), so bridge catalog → bodymap before
    // the index. Legacy bodymap-keyed rows survive via normalize.
    const bodymapVolume = catalogVolumeToBodymap(
      normalizeVolumeByMuscle(s.result.volumeByMuscle),
    );
    const muscleVol = bodymapVolume[muscleSlug] ?? 0;
    if (muscleVol === 0) continue;
    const idx = bucketEnds.findIndex((end) => s.startedAtMs <= end);
    const bucketIdx = idx === -1 ? bucketEnds.length - 1 : idx;
    volumes[bucketIdx] = (volumes[bucketIdx] ?? 0) + muscleVol;
  }
  const painEvents = painHistory
    .filter((e) => {
      const tMs = Date.parse(e.at);
      if (!Number.isFinite(tMs)) return false;
      if (tMs < cutoff || tMs > nowMs) return false;
      return e.map[muscleSlug] !== undefined;
    })
    .map((e) => ({
      atMs: Date.parse(e.at),
      severity: e.map[muscleSlug]!,
    }))
    .sort((a, b) => a.atMs - b.atMs);
  return {
    volumeBuckets: bucketEnds.map((bucketEndMs, i) => ({
      bucketEndMs,
      volumeKg: volumes[i] ?? 0,
    })),
    painEvents,
  };
};

// =========================================================================
// Tier 1 #4 — Baseline comparison.
// =========================================================================
// Thresholds pinned to the spec (progress-redesign.md §4.3):
//   ratio ≤ 1.30          → ok
//   1.30 < ratio ≤ 1.60   → warn
//   ratio > 1.60          → alarm   ("+60% vs baseline" — canonical)
// Direction matters: only spikes above baseline alarm; a dip (ratio <
// 1.0) just reads as ok (the dashboard's regression detector handles
// the "you stopped training this muscle" angle).

export type BaselineSeverity = "ok" | "warn" | "alarm";

export type BaselineCompare = {
  pctDelta: number; // signed, fraction (0.6 = +60%)
  ratio: number; // current / baseline
  severity: BaselineSeverity;
};

export const compareToBaseline = (
  current28dKg: number,
  baselineKg: number,
): BaselineCompare => {
  if (baselineKg <= 0) {
    return { pctDelta: 0, ratio: 0, severity: "ok" };
  }
  const ratio = current28dKg / baselineKg;
  const pctDelta = ratio - 1;
  let severity: BaselineSeverity;
  if (ratio > 1.6) severity = "alarm";
  else if (ratio > 1.3) severity = "warn";
  else severity = "ok";
  return { pctDelta, ratio, severity };
};
